import React from "react";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";

import { css } from "@emotion/react";

import { HeaderComponent } from "../../../components/HeaderComponent/HeaderComponent";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { FooterComponent } from "../../../components/FooterComponent/FooterComponent";
import { EventPageData, ExtractSwarovskiPageData } from "@swo/storefront";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { HeaderSpacer } from "../../_layout-helpers/spacers";
import { SimpleAspectRatio } from "../../../components/_shared-components/SimpleAspectRatio";
import { MapElementWithPin } from "../../../components/DealerLocatorDetailsComponent/MapElementWithPin";
import { SwarovskiLink } from "../../../components/_shared-components/ui-elements/SwarovskiLink";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { CSS_MQ } from "../../../styles/breakpoints";
import { getEventDates } from "../../../domain/swarovski-optik/events";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";
import { useStore } from "../../../store/hooks/use-store";
import { getLocaleWithInternationalFallback } from "@swo/shared";
import { LeftArrowSvg } from "../../../assets";
import { transformImageIfFromContentful_jpg } from "../../../domain/contentful/transform-image-if-from-contentful";
import { T } from "../../../components/T";
import { EventMetadata } from "./EventMetadata";

export const EventPageLayout: ReactTFC<ExtractSwarovskiPageData<EventPageData>> = props => {
  const { googleApi } = useStore().state.config;

  const localeWithInternationalFallback = getLocaleWithInternationalFallback(props.locale);

  function getMiniHeadlineLabel(): string {
    return getEventDates(props.data.event.startDate, props.data.event.endDate, localeWithInternationalFallback);
  }

  const miniHeadlineLabel = getMiniHeadlineLabel();

  const eventImageUrl = props.data.event.imageUrl;

  const eventImageUrl_main = transformImageIfFromContentful_jpg(eventImageUrl, 800);

  return (
    <>
      <EventMetadata event={props.data.event} pathname={props.page.path} />

      <HeaderComponent header={props.data.header} version={"normal"} />
      <HeaderSpacer />

      <ContentContainer className={"expand"}>
        <div
          className={props.data.event.coordinates ? undefined : "with-no-map"}
          css={css`
            display: grid;

            grid-row-gap: 20px;
            ${CSS_MQ.atLeastMd} {
              grid-template-columns: repeat(2, minmax(0, 1fr));
              grid-row-gap: 50px;
            }

            --aspect-ratio: calc(6 / 7);
            &.with-no-map {
              --aspect-ratio: calc(3 / 7);
            }
          `}
        >
          <SimpleAspectRatio
            css={css`
              ${CSS_MQ.atMostSm} {
                order: -1;
              }

              .with-no-map & {
                ${CSS_MQ.atLeastMd} {
                  grid-column: 1 / span 2;
                }
              }
            `}
          >
            <div
              css={css`
                position: relative;
                overflow: hidden;

                &:before {
                  content: "";
                  position: absolute;
                  width: 100%;
                  height: 100%;
                  top: 0;
                  left: 0;

                  background: url(${eventImageUrl_main});
                  background-size: cover;
                  filter: blur(70px);
                }
              `}
            >
              <img
                src={eventImageUrl_main}
                css={css`
                  position: relative;

                  width: 100%;
                  height: 100%;
                  object-fit: contain;
                  object-position: center;
                `}
              />
            </div>
          </SimpleAspectRatio>

          {props.data.event.coordinates && (
            <SimpleAspectRatio
              css={css`
                ${CSS_MQ.atMostSm} {
                  order: 4;
                }
              `}
            >
              <MapElementWithPin
                coordinates={props.data.event.coordinates}
                name={props.data.event.name}
                apiKey={googleApi.key}
              />
            </SimpleAspectRatio>
          )}

          <div
            css={css`
              ${CSS_MQ.atLeastMd} {
                padding-left: 25px;
                padding-right: 25px;
              }
            `}
          >
            <span css={miniHeadlineStyles}>{miniHeadlineLabel}</span>
            <span
              css={css`
                font-family: MinervaModern;
                color: ${CSS_COLORS.Green};

                font-size: 24px;
                letter-spacing: 0.4px;
                line-height: 26px;

                ${CSS_MQ.atLeastMd} {
                  font-size: 48px;
                  letter-spacing: 2.4px;
                  line-height: 48px;
                }

                word-break: break-word;

                display: block;

                margin-bottom: 20px;
                ${CSS_MQ.atLeastMd} {
                  margin-bottom: 58px;
                }

                text-transform: uppercase;
                word-break: break-word;
              `}
            >
              {props.data.event.name}
            </span>

            <SwarovskiLink
              link={props.data.eventOverviewPageLink}
              isInternal={true}
              styled={"underline"}
              scrollToTop={true}
            >
              <LeftArrowSvg
                css={css`
                  height: 1.5em;
                  width: auto;
                  margin-bottom: -0.5em;
                  margin-left: -0.5em;

                  color: ${CSS_COLORS.Green};
                `}
              />{" "}
              <T>{props.data.translations.goBackToEventsOverviewLinkLabel}</T>
            </SwarovskiLink>
          </div>

          <div
            css={css`
              ${CSS_MQ.atMostSm} {
                margin-top: 40px;
              }
              ${CSS_MQ.atLeastMd} {
                max-width: 35em;
                padding-left: 25px;
              }
            `}
          >
            <span css={miniHeadlineStyles}>
              {props.data.event.groupTypes.map(t => (
                <span
                  key={t}
                  css={css`
                    margin-right: 20px;
                  `}
                >
                  {t}
                </span>
              ))}
            </span>

            <p
              css={css`
                ${FONTS.SwarovskiFutura300};
                ${fontColor.black};

                font-size: 21px;
                line-height: 24px;
              `}
            >
              {props.data.event.description}
            </p>
            {props.data.event.eventExternalLink && (
              <SwarovskiButton
                as={"a"}
                link={props.data.event.eventExternalLink}
                isInternal={false}
                target={"_blank"}
                rel="noopener noreferrer"
                variant={"green"}
              >
                <T>{props.data.translations.visitWebsiteLabel}</T>
              </SwarovskiButton>
            )}
          </div>
        </div>
      </ContentContainer>

      <Spacer size={"md"} />
      <FooterComponent {...props.data.footer} />
    </>
  );
};

const miniHeadlineStyles = [
  fontSize.x2,
  css`
    text-transform: uppercase;
    display: flex;
    flex-wrap: wrap;
  `,
];
